import React, { useState, useCallback, useRef, lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Link } from 'react-router-dom';
import { FaTwitter, FaYoutube, FaInstagram, FaTiktok, FaFacebookF } from 'react-icons/fa';
import translations from './i18n';
import './App.css';

// Lazy load all pages — reduces initial bundle size
const HowTo = lazy(() => import('./pages/HowTo'));
const FAQ = lazy(() => import('./pages/FAQ'));
const YoutubeDownloader = lazy(() => import('./pages/YoutubeDownloader'));
const TwitterDownloader = lazy(() => import('./pages/TwitterDownloader'));
const InstagramDownloader = lazy(() => import('./pages/InstagramDownloader'));
const TiktokDownloader = lazy(() => import('./pages/TiktokDownloader'));
const FacebookDownloader = lazy(() => import('./pages/FacebookDownloader'));
const LangPage = lazy(() => import('./pages/LangPage'));
const Blog = lazy(() => import('./pages/Blog'));
const YouTubeDownloadGuide = lazy(() => import('./pages/blog/YouTubeDownloadGuide'));
const YouTubeToMp3 = lazy(() => import('./pages/blog/YouTubeToMp3'));
const DownloadYouTubeShorts = lazy(() => import('./pages/blog/DownloadYouTubeShorts'));
const TikTokNoWatermark = lazy(() => import('./pages/blog/TikTokNoWatermark'));
const VideoFormatsGuide = lazy(() => import('./pages/blog/VideoFormatsGuide'));
const InstagramReelsDownload = lazy(() => import('./pages/blog/InstagramReelsDownload'));
const TwitterVideoDownload = lazy(() => import('./pages/blog/TwitterVideoDownload'));
const FacebookVideoDownload = lazy(() => import('./pages/blog/FacebookVideoDownload'));
const YouTube4KDownload = lazy(() => import('./pages/blog/YouTube4KDownload'));
const TikTokAudioDownload = lazy(() => import('./pages/blog/TikTokAudioDownload'));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'));
const Terms = lazy(() => import('./pages/Terms'));

const PLATFORMS = [
  { key: 'Twitter',   icon: FaTwitter,   color: '#1DA1F2', patterns: ['twitter.com', 'x.com'] },
  { key: 'Youtube',   icon: FaYoutube,   color: '#FF0000', patterns: ['youtube.com', 'youtu.be'] },
  { key: 'Instagram', icon: FaInstagram, color: '#E4405F', patterns: ['instagram.com'] },
  { key: 'TikTok',    icon: FaTiktok,    color: '#00F2EA', patterns: ['tiktok.com'] },
  { key: 'Facebook',  icon: FaFacebookF, color: '#1877F2', patterns: ['facebook.com', 'fb.watch'] },
];

const API = process.env.NODE_ENV === 'production' ? '' : 'http://localhost:3001';

function detectPlatform(url) {
  const lower = url.toLowerCase();
  return PLATFORMS.find(p => p.patterns.some(pat => lower.includes(pat))) || null;
}

function HomePage() {
  const [platform, setPlatform] = useState(null);
  const [url, setUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [formats, setFormats] = useState([]);
  const [error, setError] = useState('');
  const [videoTitle, setVideoTitle] = useState('');

  const [videoSource, setVideoSource] = useState(null);
  const [videoApi, setVideoApi] = useState('');
  const [downloading, setDownloading] = useState(null);
  const [dlProgress, setDlProgress] = useState(0);
  const [dlStatus, setDlStatus] = useState('');
  const [dlInfo, setDlInfo] = useState('');
  const [dlFileName, setDlFileName] = useState('');
  const abortRef = useRef(null);

  const handleUrlChange = useCallback((value) => {
    setUrl(value);
    const detected = detectPlatform(value);
    if (detected) {
      setPlatform(detected.key);
      setError('');
    }
  }, []);

  const handlePaste = useCallback(async () => {
    try {
      const text = await navigator.clipboard.readText();
      handleUrlChange(text);
    } catch {
      setError('Clipboard access denied.');
    }
  }, [handleUrlChange]);

  const handleFetchFormats = useCallback(async () => {
    if (!url.trim()) {
      setError('Please enter a URL.');
      return;
    }

    const detected = detectPlatform(url.trim());
    if (!detected) {
      setError('Unsupported platform. (Twitter, YouTube, Instagram, TikTok, Facebook)');
      return;
    }

    setPlatform(detected.key);
    setError('');
    setFormats([]);
    setLoading(true);
    setProgress(0);

    const interval = setInterval(() => {
      setProgress(prev => prev >= 90 ? (clearInterval(interval), 90) : prev + 10);
    }, 200);

    try {
      const response = await fetch(`${API}/api/video-info`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ url: url.trim(), platform: detected.key.toLowerCase() }),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Could not fetch video info.');
      }

      const data = await response.json();
      setVideoTitle(data.title || '');
      setFormats(data.formats || []);
      setVideoSource(data.source || null);
      setVideoApi(data.api || '');
      setProgress(100);
    } catch (err) {
      setError(
        err.message === 'Failed to fetch'
          ? 'Server is not responding. Please try again later.'
          : err.message
      );
    } finally {
      clearInterval(interval);
      setLoading(false);
    }
  }, [url]);

  const handleFormatDownload = useCallback((format) => {
    if (downloading) return;

    const detected = detectPlatform(url.trim());
    const params = new URLSearchParams({
      url: url.trim(),
      platform: (detected?.key || 'youtube').toLowerCase(),
      formatId: format.formatId,
      ext: format.ext,
      ...(videoSource && { source: videoSource }),
      ...(videoApi && { api: videoApi }),
    });

    setDownloading(format.formatId);
    setDlProgress(0);
    setDlStatus('Preparing your file...');
    setDlInfo('');
    setDlFileName('');
    setError('');

    let mockProgress = 0;
    const mockInterval = setInterval(() => {
      mockProgress += Math.random() * 3 + 0.5;
      if (mockProgress > 85) mockProgress = 85;
      setDlProgress(prev => {
        if (prev < mockProgress) return Math.round(mockProgress);
        return prev;
      });
    }, 800);

    const evtSource = new EventSource(`${API}/api/prepare?${params.toString()}`);
    abortRef.current = { close: () => { evtSource.close(); clearInterval(mockInterval); } };

    evtSource.onmessage = (e) => {
      const data = JSON.parse(e.data);

      switch (data.type) {
        case 'status':
          setDlStatus(data.message);
          if (data.fileName) setDlFileName(data.fileName);
          break;

        case 'progress': {
          clearInterval(mockInterval);
          const realPercent = Math.round(data.percent);
          setDlProgress(realPercent);
          const parts = [];
          if (data.size) parts.push(data.size);
          if (data.speed) parts.push(data.speed);
          if (data.eta) parts.push(`ETA ${data.eta}`);
          setDlInfo(parts.join(' · '));
          setDlStatus('Downloading...');
          break;
        }

        case 'done':
          clearInterval(mockInterval);
          setDlProgress(100);
          setDlStatus('Done! Saving file...');
          evtSource.close();

          const a = document.createElement('a');
          a.href = `${API}/api/file/${data.fileId}`;
          a.download = data.fileName;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);

          setTimeout(() => {
            setDownloading(null);
            setDlProgress(0);
            setDlStatus('');
            setDlInfo('');
          }, 2000);
          break;

        case 'error':
          clearInterval(mockInterval);
          evtSource.close();
          setError(data.message);
          setDownloading(null);
          break;

        default:
          break;
      }
    };

    evtSource.onerror = () => {
      clearInterval(mockInterval);
      evtSource.close();
      if (downloading) {
        setError('Connection lost.');
        setDownloading(null);
      }
    };
  }, [url, downloading]);

  const handleCancelDownload = useCallback(() => {
    if (abortRef.current) {
      abortRef.current.close();
      abortRef.current = null;
    }
    setDownloading(null);
    setDlProgress(0);
    setDlStatus('');
    setDlInfo('');
  }, []);

  const homeFaqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      { '@type': 'Question', name: 'Is HD Video Download free?', acceptedAnswer: { '@type': 'Answer', text: 'Yes, completely free. Download unlimited videos from YouTube, Twitter, Instagram, TikTok and Facebook without registration or payment.' } },
      { '@type': 'Question', name: 'What video quality is supported?', acceptedAnswer: { '@type': 'Answer', text: 'We support 240p, 360p, 480p, 720p HD, 1080p Full HD, 1440p, and 4K (2160p). You can also extract audio as MP3.' } },
      { '@type': 'Question', name: 'Do I need to install any software?', acceptedAnswer: { '@type': 'Answer', text: 'No. HD Video Download works entirely in your web browser on any device — Windows, Mac, iPhone, Android. No apps or extensions needed.' } },
      { '@type': 'Question', name: 'Can I download TikTok videos without watermark?', acceptedAnswer: { '@type': 'Answer', text: 'Yes! HD Video Download removes the TikTok watermark automatically and saves clean HD videos.' } },
      { '@type': 'Question', name: 'How do I convert YouTube to MP3?', acceptedAnswer: { '@type': 'Answer', text: 'Paste the YouTube URL, click Download, and select the MP3 option. The audio will be extracted at high quality (192kbps).' } },
    ],
  };

  return (
    <main className="app">
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(homeFaqSchema) }} />
      <div className="container">
        <h1 className="title">Video Downloader</h1>
        <p className="hero-text">Download videos from YouTube, Twitter, Instagram, TikTok & Facebook for free.</p>

        {/* Platform Toggle */}
        <div className="platform-toggle">
          {PLATFORMS.map((p) => {
            const Icon = p.icon;
            const isActive = platform === p.key;
            return (
              <button
                key={p.key}
                className={`platform-btn ${isActive ? 'active' : ''}`}
                style={isActive ? { '--platform-color': p.color } : {}}
                onClick={() => { setPlatform(p.key); setFormats([]); setError(''); setVideoTitle(''); }}
              >
                <Icon className="platform-icon" />
                <span>{p.key}</span>
              </button>
            );
          })}
        </div>

        {/* URL Input */}
        <div className="url-row">
          <input
            type="text"
            className="url-input"
            placeholder="Paste video URL here..."
            value={url}
            onChange={(e) => handleUrlChange(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleFetchFormats()}
          />
          <button className="paste-btn" onClick={handlePaste}>
            Paste
          </button>
        </div>

        {/* Download Button */}
        <button
          className="download-btn"
          onClick={handleFetchFormats}
          disabled={loading || !!downloading}
        >
          {loading ? 'Loading...' : 'Download'}
        </button>

        {/* Fetch Progress Bar */}
        {loading && (
          <div className="progress-container">
            <div className="progress-bar">
              <div className="progress-fill" style={{ width: `${progress}%` }}>
                <div className="progress-glow" />
              </div>
            </div>
            <span className="progress-text">LOADING.....</span>
          </div>
        )}

        {/* Error */}
        {error && <div className="error-msg">{error}</div>}

        {/* Video Title */}
        {videoTitle && <div className="video-title">{videoTitle}</div>}

        {/* Download Progress */}
        {downloading && (
          <div className="dl-progress-container">
            <div className="dl-progress-header">
              <div className="dl-progress-left">
                <span className="dl-progress-label">{dlStatus}</span>
                {dlFileName && <span className="dl-file-name">{dlFileName}</span>}
                {dlInfo && <span className="dl-info">{dlInfo}</span>}
              </div>
              <button className="dl-cancel-btn" onClick={handleCancelDownload}>
                Cancel
              </button>
            </div>
            <div className="progress-bar">
              <div
                className="progress-fill dl-fill"
                style={{ width: `${dlProgress}%` }}
              >
                {dlProgress > 0 && <div className="progress-glow" />}
              </div>
            </div>
            <span className="dl-percent">{dlProgress > 0 ? `${dlProgress}%` : ''}</span>
          </div>
        )}

        {/* Format Buttons */}
        {formats.length > 0 && (
          <>
            {platform === 'Youtube' && (
              <p className="download-hint">
                YouTube videos may take 1-2 minutes to process. Please be patient while we prepare your file.
              </p>
            )}
            <div className="formats-grid">
              {formats.map((f, i) => (
                <button
                  key={i}
                  className={`format-btn ${downloading === f.formatId ? 'downloading' : ''}`}
                  onClick={() => handleFormatDownload(f)}
                  disabled={!!downloading}
                >
                  <span className="format-ext">{f.ext.toUpperCase()}</span>
                  {f.quality && (
                    <span className="format-quality">
                      {f.quality} {f.label && <sup className="format-label">{f.label}</sup>}
                    </span>
                  )}
                </button>
              ))}
            </div>
          </>
        )}

      </div>

      {/* SEO Content Section */}
      <div className="seo-section">
        <h2>Free Online Video Downloader</h2>
        <p>
          HD Video Download is the easiest way to download videos from your favorite platforms.
          Save YouTube videos in HD quality up to 4K, download Twitter videos and GIFs,
          save Instagram Reels, TikTok videos without watermark, and Facebook videos — all for free,
          no registration required.
        </p>

        <div className="seo-features">
          <div className="seo-feature">
            <h3>Multiple Formats</h3>
            <p>Download in MP4 video or MP3 audio. Choose from 240p to 4K quality.</p>
          </div>
          <div className="seo-feature">
            <h3>No Installation</h3>
            <p>Works directly in your browser. No software or extensions needed.</p>
          </div>
          <div className="seo-feature">
            <h3>All Devices</h3>
            <p>Works on Windows, Mac, iPhone, Android, and any device with a browser.</p>
          </div>
        </div>

        <a href="https://t.me/hdvideo_dl_bot" target="_blank" rel="noopener noreferrer" className="telegram-banner">
          <span className="telegram-icon">✈</span>
          <div className="telegram-text">
            <strong>Try our Telegram Bot</strong>
            <span>Download videos directly in Telegram — fast & free</span>
          </div>
        </a>

        <div className="page-links">
          <Link to="/youtube-video-downloader" className="page-link">YouTube Downloader</Link>
          <Link to="/twitter-video-downloader" className="page-link">Twitter Downloader</Link>
          <Link to="/instagram-video-downloader" className="page-link">Instagram Downloader</Link>
          <Link to="/tiktok-video-downloader" className="page-link">TikTok Downloader</Link>
          <Link to="/facebook-video-downloader" className="page-link">Facebook Downloader</Link>
          <Link to="/how-to-download" className="page-link">How to Download</Link>
          <Link to="/faq" className="page-link">FAQ</Link>
        </div>
      </div>

      {/* Footer */}
      <footer className="footer">
        <div className="footer-links">
          <Link to="/">Home</Link>
          <Link to="/how-to-download">How It Works</Link>
          <Link to="/faq">FAQ</Link>
          <Link to="/blog">Blog</Link>
          <Link to="/privacy">Privacy Policy</Link>
          <Link to="/terms">Terms of Service</Link>
        </div>
        <span>&copy; {new Date().getFullYear()} hdvideodownload.com — All rights reserved.</span>
        <a href="mailto:hdvideodownload44@gmail.com" className="footer-mail">hdvideodownload44@gmail.com</a>
      </footer>
    </main>
  );
}

function App() {
  return (
    <BrowserRouter>
      <Suspense fallback={<div className="app" />}>
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/how-to-download" element={<HowTo />} />
        <Route path="/faq" element={<FAQ />} />
        <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/youtube-video-downloader" element={<YoutubeDownloader />} />
        <Route path="/twitter-video-downloader" element={<TwitterDownloader />} />
        <Route path="/instagram-video-downloader" element={<InstagramDownloader />} />
        <Route path="/tiktok-video-downloader" element={<TiktokDownloader />} />
        <Route path="/facebook-video-downloader" element={<FacebookDownloader />} />
        <Route path="/blog" element={<Blog />} />
        <Route path="/blog/how-to-download-youtube-videos" element={<YouTubeDownloadGuide />} />
        <Route path="/blog/youtube-to-mp3" element={<YouTubeToMp3 />} />
        <Route path="/blog/download-youtube-shorts" element={<DownloadYouTubeShorts />} />
        <Route path="/blog/download-tiktok-without-watermark" element={<TikTokNoWatermark />} />
        <Route path="/blog/best-video-formats-mp4-webm" element={<VideoFormatsGuide />} />
        <Route path="/blog/download-instagram-reels" element={<InstagramReelsDownload />} />
        <Route path="/blog/download-twitter-videos" element={<TwitterVideoDownload />} />
        <Route path="/blog/download-facebook-videos" element={<FacebookVideoDownload />} />
        <Route path="/blog/download-youtube-4k" element={<YouTube4KDownload />} />
        <Route path="/blog/download-tiktok-audio" element={<TikTokAudioDownload />} />
        <Route path="/tr" element={<LangPage t={translations.tr} />} />
        <Route path="/es" element={<LangPage t={translations.es} />} />
        <Route path="/fr" element={<LangPage t={translations.fr} />} />
        <Route path="/pt" element={<LangPage t={translations.pt} />} />
        <Route path="/ar" element={<LangPage t={translations.ar} />} />
        <Route path="/zh" element={<LangPage t={translations.zh} />} />
        <Route path="/ru" element={<LangPage t={translations.ru} />} />
        <Route path="/id" element={<LangPage t={translations.id} />} />
        <Route path="/ms" element={<LangPage t={translations.ms} />} />
        <Route path="/bn" element={<LangPage t={translations.bn} />} />
        <Route path="/hi" element={<LangPage t={translations.hi} />} />
        <Route path="/ja" element={<LangPage t={translations.ja} />} />
        <Route path="/ko" element={<LangPage t={translations.ko} />} />
        <Route path="/de" element={<LangPage t={translations.de} />} />
        <Route path="/it" element={<LangPage t={translations.it} />} />
      </Routes>
      </Suspense>
    </BrowserRouter>
  );
}

export default App;
